import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useQRLogin } from 'shared-ui';
import { API_BASE_URL } from '../../services/api';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginMode, setLoginMode] = useState<'password' | 'qr'>('password');
  const [verifiedMessage, setVerifiedMessage] = useState(false);

  const { qrDataUrl } = useQRLogin({
    apiBaseUrl: API_BASE_URL,
    loginContext: 'personal',
    tokenStorageKey: 'mangoPersonalToken',
    enabled: loginMode === 'qr',
    onError: setError,
  });

  useEffect(() => {
    if (searchParams.get('verified') === '1') setVerifiedMessage(true);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const success = await login(email, password);
    if (success) {
      navigate('/');
    } else {
      setError('Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <span style={{ fontSize: '56px' }}>🥭</span>
          <h1 style={{ margin: '15px 0 5px 0', fontSize: '28px', color: '#333' }}>Welcome Back</h1>
          <p style={{ margin: 0, color: '#666', fontSize: '15px' }}>Sign in to your Mango Personal account</p>
        </div>

        {verifiedMessage && (
          <div style={{
            padding: '12px',
            background: '#e8f5e9',
            color: '#2e7d32',
            borderRadius: '10px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            Email verified! You can now log in.
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '10px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => setLoginMode('password')}
            style={{
              flex: 1,
              padding: '8px',
              background: loginMode === 'password' ? '#ff6b35' : '#eee',
              color: loginMode === 'password' ? 'white' : '#333',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => setLoginMode('qr')}
            style={{
              flex: 1,
              padding: '8px',
              background: loginMode === 'qr' ? '#ff6b35' : '#eee',
              color: loginMode === 'qr' ? 'white' : '#333',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            QR Code
          </button>
        </div>

        {loginMode === 'qr' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {qrDataUrl ? (
              <>
                <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>Scan with Mango Login app</p>
                <img src={qrDataUrl} alt="QR Code" style={{ display: 'block', margin: '0 auto 16px' }} />
                <p style={{ fontSize: '12px', color: '#999' }}>Open the app and choose your account</p>
              </>
            ) : (
              <p style={{ color: '#666' }}>Loading QR code...</p>
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#333' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '16px',
                transition: 'border 0.2s'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                Password
              </label>
              <Link to="/forgot-password" style={{ fontSize: '13px', color: '#ff6b35', textDecoration: 'none' }}>
                Forgot password?
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '16px'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        )}

        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: '#ff6b35', fontWeight: '600', textDecoration: 'none' }}>
            Create one
          </Link>
        </p>

        <div style={{ 
          marginTop: '30px', 
          paddingTop: '25px', 
          borderTop: '1px solid #eee',
          textAlign: 'center'
        }}>
          <p style={{ color: '#999', fontSize: '12px', margin: 0 }}>
            By signing in, you agree to Mango's Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
